//Built-in libraries

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
//Third party libraries
import * as Yup from 'yup';
//Custom components
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { patchProject } from '../../../../../../../api/Sherpa/Project';
//Assets and Miscellaneous
import type { IPatchProjectServicePayload } from '../../../../../../../api/Sherpa/Project/modules/ProjectCore/types';
import { SherpaProjectReactQueryKey } from '../../../../../../../constants/ReactQueryKeys';
import { useProject } from '../../../../../../../hooks/ReactQueryHooks/useProjectQuery';
import { useUsers } from '../../../../../../../hooks/ReactQueryHooks/useUsersQuery';
//Services
import { notifyUser } from '../../../../../../../store/Toast';
import type { User } from '../../../../../../../types/User';
import { sanitizeResponseError } from '../../../../../../../utils';

//Component Begin

interface IProjectLeadFormData {
  projectLead: string;
}

interface IChangeProjectLeadProps {
  handleModal: () => void;
}

interface IProjectLeadFormProps extends IChangeProjectLeadProps {
  currentProjectLead?: number;
  validProjectLeadOptions: Array<{ label: string; value: string }>;
}

const ProjectLeadForm = ({ handleModal, currentProjectLead, validProjectLeadOptions }: IProjectLeadFormProps) => {
  const { projectId } = useParams<string>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const updateProjectLead = useMutation(
    (data: Partial<IPatchProjectServicePayload>) => patchProject({ projectId: Number(projectId), data }),
    {
      onSuccess: () => {
        handleModal();
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Lead changed successfully.',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const onSubmit = (formData: IProjectLeadFormData) => {
    if (currentProjectLead === parseInt(formData.projectLead, 10)) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'No change was detected to change project lead.',
          toastType: 'info',
        })
      );
      handleModal();
    } else {
      updateProjectLead.mutate({
        projectLeadId: Number(formData.projectLead),
      });
    }
  };

  return (
    <HookForm onSubmit={onSubmit}>
      <h2 className='mt-0'>Change Project Lead</h2>
      <HookReactSingleSelect id='change-project-lead' name='projectLead' label='Project Lead' options={validProjectLeadOptions} />
      <div className='flex justify-end gap-4 mt-5'>
        <Button label='Cancel' btnStyle='neutral' type='button' onClick={handleModal} />
        <Button
          label={`${updateProjectLead.isLoading ? 'Saving...' : 'Save'}`}
          btnStyle='primary'
          type='submit'
          disabled={updateProjectLead.isLoading}
        />
      </div>
    </HookForm>
  );
};

const ChangeProjectLead = (props: IChangeProjectLeadProps) => {
  const { projectId } = useParams<string>();
  const fetchProject = useProject({ projectId: Number(projectId) });

  const fetchUsers = useUsers({ params: { active: 'true' } });
  const users = fetchUsers.data?.data;

  const authorizedProjectLeadUsers: User[] = [];
  const authorizedProjectLeadRoles = ['SUPER ADMIN', 'ADMIN', 'PROJECT MANAGER'];
  users?.forEach((user: User) => {
    if (authorizedProjectLeadRoles.includes(user.role.toUpperCase())) {
      authorizedProjectLeadUsers.push(user);
    }
  });
  const validProjectLeadOptions = authorizedProjectLeadUsers.map(({ id, name, email }) => ({
    label: `${name} <${email}>`,
    value: id.toString(),
  }));

  const validationSchema = Yup.object({
    projectLead: Yup.string()
      .required('Project Lead is required')
      .transform((val) => val.value),
  });

  const currentProjectLead = fetchProject?.data?.data?.projectLead;

  const transformedDefaultValues = {
    projectLead: {
      label: `${currentProjectLead?.name} <${currentProjectLead?.email}>`,
      value: currentProjectLead?.id.toString(),
    },
  };

  return (
    <Modal>
      <HookFormProvider defaultValues={transformedDefaultValues} resolver={yupResolver(validationSchema) as any} mode='all'>
        <ProjectLeadForm {...props} currentProjectLead={currentProjectLead?.id} validProjectLeadOptions={validProjectLeadOptions} />
      </HookFormProvider>
    </Modal>
  );
};

export default ChangeProjectLead;
